import { useEffect, useState } from 'react'
import { Paperclip } from 'lucide-react'
import { api } from '@/lib/api'

interface Attachment {
  s3Key: string
  filename: string
  mimeType: string
  size: number
}

/**
 * Image attachments need a presigned URL because <img src=...> can't carry
 * Authorization headers. We fetch the JSON variant of the download endpoint
 * with auth, then use the returned URL directly.
 */
function useAttachmentUrl(ticketId: string, a: Attachment) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!a.mimeType.startsWith('image/')) return
    let cancelled = false
    api<{ url: string }>(
      `/support-tickets/${ticketId}/attachments/${encodeURIComponent(a.s3Key)}`,
      { query: { json: '1' } },
    )
      .then(res => { if (!cancelled) setUrl(res.url) })
      .catch(() => { /* leave url null; image won't render */ })
    return () => { cancelled = true }
  }, [ticketId, a.s3Key, a.mimeType])
  return url
}

function AttachmentImage({ ticketId, a }: { ticketId: string; a: Attachment }) {
  const url = useAttachmentUrl(ticketId, a)
  if (!url) {
    return <span className="text-[11px] text-muted-foreground">{a.filename} (loading…)</span>
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block">
      <img src={url} alt={a.filename} className="max-h-48 rounded border border-border object-cover" />
    </a>
  )
}

function AttachmentDownloadLink({ ticketId, a }: { ticketId: string; a: Attachment }) {
  const apiBase = import.meta.env.VITE_API_URL ?? ''
  // Non-image: a regular click-through that triggers the 302 download flow.
  // We can't pre-resolve it the same way (browser would need to send auth).
  const onClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    try {
      const res = await api<{ url: string }>(
        `/support-tickets/${ticketId}/attachments/${encodeURIComponent(a.s3Key)}`,
        { query: { json: '1' } },
      )
      window.open(res.url, '_blank', 'noreferrer')
    } catch {
      // ignore — toast handled by ofetch's onResponseError if mounted
    }
  }
  return (
    <a
      href={`${apiBase}/support-tickets/${ticketId}/attachments/${encodeURIComponent(a.s3Key)}`}
      onClick={onClick}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-[7px] rounded-etn-menu border border-etn-border-hairline bg-etn-surface-sunken px-2.5 py-1.5 text-etn-micro font-semibold text-etn-text-label hover:bg-etn-surface hover:text-etn-text-cell"
    >
      <Paperclip className="h-3 w-3 text-etn-text-soft" />
      {a.filename}
    </a>
  )
}

export function AttachmentList({
  ticketId,
  attachments,
}: {
  ticketId: string
  attachments: Attachment[]
}) {
  if (!attachments.length) return null
  return (
    <ul className="mt-2 flex flex-wrap gap-2">
      {attachments.map(a => (
        <li key={a.s3Key}>
          {a.mimeType.startsWith('image/')
            ? <AttachmentImage ticketId={ticketId} a={a} />
            : <AttachmentDownloadLink ticketId={ticketId} a={a} />}
        </li>
      ))}
    </ul>
  )
}
